import clsx from 'clsx';
import { Check, X } from 'lucide-react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

interface IntegrationRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const IntegrationRequestModal: React.FC<IntegrationRequestModalProps> = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        toolName: '',
        toolCategory: '',
        toolWebsite: '',
        reason: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    if (!isOpen) return null;

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
    
        if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.toolName.trim()) newErrors.toolName = 'Tool/Platform Name is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    
        if (validateForm()) {
            console.log("Integration Request Form submitted", formData);
            setIsSubmitted(true);
            setErrors({});
        }
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setIsSubmitted(false);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                toolName: '',
                toolCategory: '',
                toolWebsite: '',
                reason: ''
            });
        }, 200);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined } as any));
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
            <div 
                className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
                onClick={handleClose}
            />
      
            <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <button 
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-2 text-text-sub hover:text-text-main rounded-full hover:bg-gray-100 transition-colors z-10"
                >
                    <X size={20} />
                </button>

                <div className="overflow-y-auto custom-scrollbar">
                    <div className="p-8 sm:p-10">
                        {isSubmitted ? (
                            <div className="flex flex-col items-center justify-center text-center py-10 animate-in fade-in duration-300">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
                                    <Check size={40} className="text-green-600" strokeWidth={3} />
                                </div>
                
                                <h2 className="text-3xl font-bold text-text-main mb-4">Request Submitted!</h2>
                                <p className="text-text-sub max-w-md mx-auto mb-10 leading-relaxed text-lg">
                                    We'll track this request and notify you when it's available.
                                </p>
                                <button
                                    onClick={handleClose}
                                    className="px-8 py-3 bg-text-main text-white font-bold rounded-xl hover:bg-text-main/90 transition-colors shadow-lg"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="text-center mb-10">
                                    <h2 className="text-3xl font-bold text-text-main mb-3">Request an Integration</h2>
                                    <p className="text-text-sub text-lg">
                                        Tell us which tool you'd like to connect with Bustly and we'll prioritize adding it.
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-text-main">
                                                First Name
                                                <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                className={clsx(
                                                    "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                                                    errors.firstName ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                                                )}
                                                placeholder="First name"
                                            />
                                            {errors.firstName ? <p className="text-red-500 text-xs mt-1">{errors.firstName}</p> : null}
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-text-main">
                                                Last Name
                                                <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                className={clsx(
                                                    "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                                                    errors.lastName ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                                                )}
                                                placeholder="Last name"
                                            />
                                            {errors.lastName ? <p className="text-red-500 text-xs mt-1">{errors.lastName}</p> : null}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-text-main">
                                            Work Email
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={clsx(
                                                "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                                                errors.email ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                                            )}
                                            placeholder="you@company.com"
                                        />
                                        {errors.email ? <p className="text-red-500 text-xs mt-1">{errors.email}</p> : null}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-text-main">
                                            Tool/Platform Name
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="toolName"
                                            value={formData.toolName}
                                            onChange={handleChange}
                                            className={clsx(
                                                "w-full px-4 py-3 rounded-xl border bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20",
                                                errors.toolName ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-[#1A162F]"
                                            )}
                                            placeholder="e.g., Notion, Airtable, Monday.com"
                                        />
                                        {errors.toolName ? <p className="text-red-500 text-xs mt-1">{errors.toolName}</p> : null}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-text-main">Tool Category</label>
                                        <select
                                            name="toolCategory"
                                            value={formData.toolCategory}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20 appearance-none"
                                        >
                                            <option value="">Select a category (optional)</option>
                                            <option value="ecommerce">E-commerce</option>
                                            <option value="marketing">Marketing & Advertising</option>
                                            <option value="analytics">Analytics</option>
                                            <option value="crm">CRM & Support</option>
                                            <option value="finance">Finance & Accounting</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-text-main">Tool Website</label>
                                        <input
                                            type="url"
                                            name="toolWebsite"
                                            value={formData.toolWebsite}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#1A162F] transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                                            placeholder="https://example.com (optional)"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-text-main">Why do you need this integration?</label>
                                        <textarea
                                            name="reason"
                                            value={formData.reason}
                                            onChange={handleChange}
                                            rows={3}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#1A162F] transition-colors outline-none focus:ring-2 focus:ring-[#1A162F]/20 resize-none"
                                            placeholder="Tell us how you would use this integration... (optional)"
                                        />
                                    </div>

                                    <div className="pt-4">
                                        <button
                                            type="submit"
                                            className="w-full py-4 bg-text-main text-white font-bold rounded-xl hover:bg-text-main/90 transition-colors shadow-lg flex items-center justify-center gap-2 group"
                                        >
                                            <span>Submit Request</span>
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default IntegrationRequestModal;
